import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The rendered size of an element, kept current through a `ResizeObserver`.
 *
 * Returned as a callback ref rather than taking one, so the caller does not
 * have to arrange for the observer to be re-attached when React hands it a
 * different element — a popover that unmounts and remounts on the next pin, for
 * instance. Attaching and detaching happen in the ref callback itself, which is
 * the only place that reliably knows an element has been swapped.
 *
 * `{ width: 0, height: 0 }` before the first measurement is meaningful: it says
 * "not measured yet", and callers that would otherwise position something
 * against a size they do not have use it to stay hidden for that one frame.
 */
export interface ElementSize {
  readonly width: number;
  readonly height: number;
}

const UNMEASURED: ElementSize = { width: 0, height: 0 };

export interface UseElementSizeResult {
  readonly ref: (element: HTMLElement | null) => void;
  readonly size: ElementSize;
}

export const useElementSize = (): UseElementSizeResult => {
  const [size, setSize] = useState<ElementSize>(UNMEASURED);
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    },
    [],
  );

  const ref = useCallback((element: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (element === null) {
      setSize(UNMEASURED);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (entry === undefined) {
        return;
      }

      const { width, height } = entry.contentRect;

      /*
       * Border-box, not content-box: the caller positions the element's outer
       * edge, and a popover's border and padding are part of what has to fit
       * inside the frame.
       */
      const box = entry.borderBoxSize[0];

      setSize({
        width: box?.inlineSize ?? width,
        height: box?.blockSize ?? height,
      });
    });

    observer.observe(element);
    observerRef.current = observer;
  }, []);

  return { ref, size };
};
